<template>
  <section
    class="flex flex-col overflow-hidden items-center justify-center overflow-hidden px-4 pb-6 pt-10 md:py-14 bg-gradient-to-b to-transparent from-surface-light relative"
  >
    <div v-for="(media, idx) in slides" :key="idx">
      <transition name="fade">
        <img
          v-if="media && currentSlide === idx"
          :src="media"
          :alt="event.info1"
          :class="[
            'blur-xl w-full h-full object-cover absolute inset-0 -z-1 scale-105 origin-top',
          ]"
        />
      </transition>
    </div>

    <div class="max-w-[1060px] w-full text-center">
      <BaseButton color="muted" to="/">
        <DropdownIcon class="rotate-90 h-4 w-4 mr-2 fill-text" />
        {{ $t('common.back_to_overview') }}
      </BaseButton>

      <h1
        :class="[
          'animate-appear mb-6 mt-2',
          getLocalizedEventValue(event, locale, 'info1').length > 50 ? 'h1-small' : 'h1',
        ]"
      >
        {{ getLocalizedEventValue(event, locale, 'info1') }}
      </h1>
      <div v-if="badges.length" class="mb-8 space-x-1">
        <button
          v-for="(badge, index) in badges"
          :key="index"
          :class="'bg-warning font-semibold text-sm px-3 py-1 rounded ' + badge.class"
        >
          {{ $t(badge.localeKey) }}
        </button>
      </div>

      <div class="relative mt-12">
        <button
          v-if="!isBeginning"
          @click="prevSlide"
          type="button"
          role="button"
          :title="$t('common.prev')"
          :class="[
            'z-10 -translate-y-1/2 -translate-x-1/2 absolute left-0 top-1/2 cursor-pointer w-12 h-12 hidden md:flex items-center justify-center rounded-full bg-surface-grey-light md:bg-surface-light hover:bg-surface-grey transition',
            isBeginning ? 'opacity-50 cursor-not-allowed' : '',
          ]"
        >
          <ArrowRightIcon class="rotate-180" />
        </button>
        <button
          v-if="!isEnd"
          type="button"
          @click="nextSlide"
          role="button"
          :title="$t('common.next')"
          :class="[
            '-translate-y-1/2 translate-x-1/2 z-10 absolute right-0 top-1/2 cursor-pointer w-12 h-12 hidden md:flex items-center justify-center rounded-full bg-surface-grey-light md:bg-surface-light hover:bg-surface-grey transition',
            isEnd ? 'opacity-50 cursor-not-allowed' : '',
          ]"
        >
          <ArrowRightIcon />
        </button>

        <swiper-container
          v-if="slides"
          class="swiper-overflow-visible swiper-hero"
          :slides-per-view="1"
          :space-between="48"
          :initialSlide="slides.length > 1 ? 1 : 0"
          ref="swiperRef"
          :on:swiperslidechange="onSlideChange"
        >
          <swiper-slide v-for="(slide, idx) in slides" :key="idx">
            <div class="relative rounded-md overflow-hidden">
              <img
                :src="slide"
                :alt="event.info1"
                class="w-full h-auto object-cover aspect-video"
              />
              <button
                v-if="event.video_url && idx == (slides.length > 1 ? 1 : 0)"
                @click="showVideoModal = true"
                class="text-sm md:text-base cursor-pointer hover:opacity-80 transition-opacity font-semibold absolute bottom-2 left-2 md:bottom-4 md:left-4 bg-surface-light rounded-sm px-2 py-1 md:px-3 md:py-2 flex text-text items-center"
              >
                <span class="border border-surface-grey rounded-full mr-2">
                  <PlayIcon class="h-6 w-6 md:h-8 md:w-8" />
                </span>
                {{ $t('event.play_video') }}
              </button>
            </div>
          </swiper-slide>
        </swiper-container>
      </div>

      <div
        class="hidden md:flex gap-2 justify-center mt-4"
        v-if="slides && slides.length > 1"
      >
        <button
          v-for="(media, idx) in slides"
          :key="media"
          type="button"
          role="button"
          :aria-label="$t('event.go_to_slide', { slide: idx + 1 })"
          @click="goToSlide(idx)"
          class="items-center justify-center h-2 rounded-full bg-surface-grey-light hover:bg-surface-grey origin-center duration-500 transition-all"
          :class="{
            'opacity-100 w-9': currentSlide === idx,
            'opacity-20 w-2 mix-blend-difference': currentSlide !== idx,
          }"
        />
      </div>

      <!-- Thumbnail slider -->
      <swiper-container
        v-if="slides && slides.length > 1"
        class="w-full mt-6 swiper-overflow-visible md:hidden"
        slides-per-view="auto"
        :space-between="8"
        ref="thumbsRef"
        @click="onThumbClick"
      >
        <swiper-slide
          v-for="(thumb, idx) in slides"
          :key="thumb"
          :class="[
            'items-center cursor-pointer w-[100px]',
            currentSlide === idx ? 'opacity-100' : 'opacity-70',
          ]"
          @click.stop="goToSlide(idx)"
        >
          <img
            :src="thumb"
            :alt="event.info1"
            class="rounded-md w-full aspect-video object-cover mb-2"
          />

          <span
            v-if="currentSlide === idx"
            class="block w-2 h-2 mx-auto rounded-full bg-primary mt-1"
          ></span>
        </swiper-slide>
      </swiper-container>

      <BaseModal v-if="event.video_url" v-model="showVideoModal" size="fit" background="transparent">
        <div v-if="event.video_url.endsWith('.mp4')" class="aspect-video">
          <video
            controls
            autoplay
            class="aspect-video h-full w-full object-contain block"
          >
            <source :src="event.video_url" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>

        <div v-else class="overflow-hidden bg-transparent" style="width: min(90vw, calc(85vh * 16 / 9))">
          <iframe
            :src="getVideoEmbedUrl(event.video_url)"
            frameborder="0"
            allowfullscreen
            class="w-full aspect-video block"
          ></iframe>
        </div>
      </BaseModal>
    </div>
  </section>
</template>

<script setup lang="ts">
  import type { SwiperContainer } from 'swiper/element';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useI18n } from 'vue-i18n';

  import type { EventDetail } from '@/types';
  import { getBadgesForEvent, getLocalizedEventValue } from '@/utils/eventItemUtil';
  import { photoToUrl } from '@/utils/photoToUrl';

  import ArrowRightIcon from '../icons/ArrowRightIcon.vue';
  import DropdownIcon from '../icons/DropdownIcon.vue';
  import PlayIcon from '../icons/PlayIcon.vue';
  import BaseButton from '../ui/BaseButton.vue';
  import BaseModal from '../ui/BaseModal.vue';

  const { event } = defineProps<{ event: EventDetail }>();
  const { locale } = useI18n();
  const { t: $t } = useI18n();
  const swiperRef = ref<SwiperContainer | null>(null);
  const thumbsRef = ref<SwiperContainer | null>(null);
  const isBeginning = ref(true);
  const isEnd = ref(false);
  const showVideoModal = ref(false);
  const currentSlide = ref(0);

  // Merge main photo and extra photos. If no extra photos, just run photoToUrl on main photo, this will return a default image path.
  const slides = computed(() => [
    ...((event.photo || !event.extra_photos || event.extra_photos.length === 0) ? [photoToUrl(event.photo)] : []),
    ...(event.extra_photos ? event.extra_photos.map(photoToUrl) : []),
  ]);

  const badges = computed(() =>
    getBadgesForEvent(event).filter(badge => badge.class !== 'badge-status')
  );

  function updateNavState() {
    const swiper = swiperRef.value?.swiper;
    if (swiper) {
      isBeginning.value = swiper.isBeginning;
      isEnd.value = swiper.isEnd;
      currentSlide.value = swiper.activeIndex;
    }
  }

  function nextSlide() {
    if (swiperRef.value) {
      swiperRef.value.swiper.slideNext();
    }
  }

  function prevSlide() {
    if (swiperRef.value) {
      swiperRef.value.swiper.slidePrev();
    }
  }

  function onSlideChange() {
    updateNavState();

    if (thumbsRef.value && thumbsRef.value.swiper) {
      thumbsRef.value.swiper.slideTo(currentSlide.value, 300, true);
    }
  }

  function goToSlide(idx: number) {
    if (swiperRef.value) {
      swiperRef.value.swiper.slideTo(idx);
      updateNavState();
    }
  }

  function onThumbClick(e: MouseEvent) {
    e.stopPropagation();
  }

  function getVideoEmbedUrl(url: string) {
    const videoId = url.split('v=')[1];
    const ampersandPosition = videoId?.indexOf('&');
    if (ampersandPosition !== -1) {
      return `https://www.youtube.com/embed/${videoId?.substring(0, ampersandPosition)}`;
    }
    return `https://www.youtube.com/embed/${videoId}`;
  }

  onMounted(() => {
    updateNavState();
    // Center the initial active thumbnail
    if (thumbsRef.value && thumbsRef.value.swiper) {
      thumbsRef.value.swiper.slideTo(currentSlide.value, 0, false);
    }
  });

  watch(swiperRef, () => {
    updateNavState();
  });
</script>

<style scoped>
  .swiper-overflow-visible {
    overflow: visible;
  }
</style>
